import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { resolveTeacherPlan, TEACHER_PLANS } from '../../config/teacherPlans';

// px to rem 변환 함수
const pxToRem = (value: number) => `${value / 16}rem`;

// UserProfileMenuDialog props 인터페이스
interface UserProfileMenuDialogProps {
  isOpen: boolean;
  onClose: () => void;
  anchorElement?: HTMLElement | null;
}

const UserProfileMenuDialog: React.FC<UserProfileMenuDialogProps> = ({
  isOpen,
  onClose,
  anchorElement
}) => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const isAdminUser =
    currentUser?.role === 'superAdmin' || currentUser?.role === 'schoolAdmin';

  // 교사 계정은 이름 옆에 요금제를 보여 준다. 관리자는 항상 학교 요금제라 표시하지 않는다.
  const teacherPlanName =
    currentUser?.role === 'teacher'
      ? TEACHER_PLANS.find((plan) => plan.id === resolveTeacherPlan(currentUser.plan, currentUser.role))?.name
      : undefined;

  // 프로필 페이지로 이동
  const handleProfile = () => {
    if (currentUser?.role === 'student') {
      navigate('/class/student/StudentSettings');
    } else if (
      currentUser?.role === 'teacher' ||
      currentUser?.role === 'superAdmin' ||
      currentUser?.role === 'schoolAdmin'
    ) {
      navigate('/class/teacher/TeacherSetting');
    }
    onClose();
  };

  // 설정 페이지로 이동
  const handleSettings = () => {
    if (currentUser?.role === 'student') {
      navigate('/class/student/StudentSettings');
    } else if (
      currentUser?.role === 'teacher' ||
      currentUser?.role === 'superAdmin' ||
      currentUser?.role === 'schoolAdmin'
    ) {
      navigate('/class/teacher/TeacherSetting');
    }
    onClose();
  };

  const handleAdminNav = (path: string) => {
    navigate(path);
    onClose();
  };

  // 로그아웃 처리
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('로그아웃 중 오류 발생:', error);
    }
    onClose();
  };

  if (!isOpen || !mounted) return null;

  const handleLogin = () => {
    navigate('/login', { state: { from: { pathname: location.pathname } } });
    onClose();
  };

  // 앵커 엘리먼트의 위치 계산
  const getPosition = () => {
    if (anchorElement) {
      const rect = anchorElement.getBoundingClientRect();
      return {
        top: `${rect.bottom + 8}px`,
        right: `${window.innerWidth - rect.right - 16}px`
      };
    }
    // 기본 위치: 네비게이션 바로 아래 오른쪽
    return {
      top: '5rem',
      right: '1.25rem'
    };
  };

  const position = getPosition();

  return createPortal(
    <>
      {/* 배경 오버레이 — body에 붙여 레이아웃 overflow/scale에 가리지 않음 */}
      <div
        role="presentation"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.08)',
          zIndex: 99980
        }}
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
      />
      
      <div
        role="menu"
        aria-modal="true"
        style={{
          position: 'fixed',
          top: position.top,
          right: position.right,
          width: pxToRem(260),
          maxWidth: 'calc(100vw - 24px)',
          background: 'var(--Color-background-normal, white)',
          borderRadius: pxToRem(8),
          boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
          padding: pxToRem(8),
          display: 'flex',
          flexDirection: 'column',
          gap: pxToRem(4),
          zIndex: 99990,
          fontFamily: 'Pretendard, sans-serif',
          border: '1px solid var(--Color-line-normal, #F0F0F0)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {!currentUser ? (
          <button
            type="button"
            onClick={handleLogin}
            style={{
              padding: `${pxToRem(10)} ${pxToRem(12)}`,
              borderRadius: pxToRem(6),
              border: 'none',
              background: '#FF6F0F',
              color: '#FFFFFF',
              fontSize: pxToRem(14),
              fontWeight: 600,
              lineHeight: pxToRem(20),
              textAlign: 'center',
              cursor: 'pointer',
              width: '100%'
            }}
          >
            로그인
          </button>
        ) : (
        <>
        {/* 사용자 정보 (선택사항) */}
        {currentUser && (
          <div
            style={{
              padding: pxToRem(12),
              borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
              marginBottom: pxToRem(4)
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: pxToRem(6),
                flexWrap: 'wrap',
                color: 'var(--Color-text-normal, #161616)',
                fontSize: pxToRem(14),
                fontWeight: 600,
                lineHeight: pxToRem(20),
                marginBottom: pxToRem(4)
              }}
            >
              <span>{currentUser.name}</span>
              {teacherPlanName && (
                <span
                  style={{
                    padding: `${pxToRem(1)} ${pxToRem(8)}`,
                    borderRadius: '999px',
                    border: '1px solid var(--Color-fill-brand-color, #FF6F0F)',
                    color: 'var(--Color-fill-brand-color, #FF6F0F)',
                    fontSize: pxToRem(11),
                    fontWeight: 600,
                    lineHeight: pxToRem(18),
                    whiteSpace: 'nowrap'
                  }}
                >
                  {teacherPlanName} 요금제
                </span>
              )}
            </div>
            <div
              style={{
                color: 'var(--Color-text-assistive, #989898)',
                fontSize: pxToRem(12),
                fontWeight: 400,
                lineHeight: pxToRem(18)
              }}
            >
              {currentUser.email}
            </div>
          </div>
        )}

        {/* 프로필 메뉴 항목 */}
        <button
          type="button"
          onClick={handleProfile}
          style={{
            padding: `${pxToRem(10)} ${pxToRem(12)}`,
            borderRadius: pxToRem(6),
            border: 'none',
            background: 'transparent',
            color: 'var(--Color-text-normal, #161616)',
            fontSize: pxToRem(14),
            fontWeight: 400,
            lineHeight: pxToRem(20),
            textAlign: 'left',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--Color-background-sub, #F8F8F8)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          프로필
        </button>

        {/* 설정 메뉴 항목 */}
        <button
          type="button"
          onClick={handleSettings}
          style={{
            padding: `${pxToRem(10)} ${pxToRem(12)}`,
            borderRadius: pxToRem(6),
            border: 'none',
            background: 'transparent',
            color: 'var(--Color-text-normal, #161616)',
            fontSize: pxToRem(14),
            fontWeight: 400,
            lineHeight: pxToRem(20),
            textAlign: 'left',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--Color-background-sub, #F8F8F8)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          설정
        </button>

        {/* 학교/시스템 관리 (학교 관리자·최고 관리자) */}
        {isAdminUser && (
          <>
            <div
              style={{
                height: pxToRem(1),
                background: 'var(--Color-line-normal, #F0F0F0)',
                margin: `${pxToRem(4)} 0`
              }}
            />
            <div
              style={{
                padding: `${pxToRem(4)} ${pxToRem(8)} ${pxToRem(2)}`,
                fontSize: pxToRem(11),
                fontWeight: 600,
                color: 'var(--Color-text-assistive, #989898)',
                letterSpacing: '0.02em'
              }}
            >
              관리 메뉴
            </div>
            {[
              { label: '학교 관리', path: '/admin/school-management' },
              { label: '교사 승인 관리', path: '/admin/teacher-approval' },
              { label: '알림 관리', path: '/admin/notifications' },
              ...(currentUser?.role === 'superAdmin'
                ? [{ label: '보안 관리', path: '/admin/security' as const }]
                : []),
              { label: '관리자 홈', path: '/admin' }
            ].map(({ label, path }) => (
              <button
                key={path}
                type="button"
                onClick={() => handleAdminNav(path)}
                style={{
                  padding: `${pxToRem(10)} ${pxToRem(12)}`,
                  borderRadius: pxToRem(6),
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--Color-text-normal, #161616)',
                  fontSize: pxToRem(14),
                  fontWeight: 400,
                  lineHeight: pxToRem(20),
                  textAlign: 'left',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease',
                  width: '100%'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--Color-background-sub, #F8F8F8)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                {label}
              </button>
            ))}
          </>
        )}

        {/* 구분선 */}
        <div
          style={{
            height: pxToRem(1),
            background: 'var(--Color-line-normal, #F0F0F0)',
            margin: `${pxToRem(4)} 0`
          }}
        />

        {/* 로그아웃 메뉴 항목 */}
        <button
          type="button"
          onClick={handleLogout}
          style={{
            padding: `${pxToRem(10)} ${pxToRem(12)}`,
            borderRadius: pxToRem(6),
            border: 'none',
            background: 'transparent',
            color: 'var(--Color-status-negative, #FF3838)',
            fontSize: pxToRem(14),
            fontWeight: 400,
            lineHeight: pxToRem(20),
            textAlign: 'left',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--Color-background-sub, #F8F8F8)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          로그아웃
        </button>
        </>
        )}
      </div>
    </>,
    document.body
  );
};

export default UserProfileMenuDialog;

